import { inject, Injectable } from '@angular/core';
import type { LoginResponse } from 'angular-auth-oidc-client';
import type { Observable } from 'rxjs';
import { EMPTY, from, map, mergeMap, of, switchMap, tap } from 'rxjs';

import { ImageApiService } from '../../pages/gallery/image-gallery/services/image-api.service';
import { ImageProcessingService } from '../../pages/gallery/image-gallery/services/image-processing.service';

import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';

/**
 * SessionService
 *
 * Owns the lifecycle logic that bridges a completed OIDC login and the app
 * being ready for use.  Specifically it answers:
 *   - Did a different user log in than was last active?
 *   - If so, clear all per-user caches and reload to a clean state.
 *   - If not, persist the new session and continue.
 *
 * Injected by GeneralEffects so the effect stays thin (action → service →
 * action) and all coordination logic lives here instead of in the effects
 * class.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly settingsService = inject(SettingsService);
  private readonly imageApiService = inject(ImageApiService);
  private readonly imageProcessingService = inject(ImageProcessingService);

  /**
   * Runs the full post-login resolution pipeline:
   *   1. Loads the previous customer ID from persistent storage.
   *   2. Calls the OIDC checkLoggedIn flow.
   *   3. If the returning user differs from the previous session, clears all
   *      per-user caches and navigates to the origin URL (hard reload).
   *   4. If the same user, persists the new session and emits the login response.
   */
  public resolveLogin$(url: string | undefined, isLoginCallback: boolean): Observable<{ loginResponse: LoginResponse; isLoginCallback: boolean }> {
    return from(this.settingsService.getLastLoggedInCustomerId$()).pipe(
      switchMap(previousCustomerId =>
        this.authenticationService
          .checkLoggedIn$(url)
          .pipe(switchMap(loginResponse => this.handleLoginResult$(previousCustomerId, loginResponse, isLoginCallback)))
      )
    );
  }

  private handleLoginResult$(
    previousCustomerId: string,
    loginResponse: LoginResponse,
    isLoginCallback: boolean
  ): Observable<{ loginResponse: LoginResponse; isLoginCallback: boolean }> {
    const newCustomerId = this.authenticationService.customerId;

    if (this.hasUserChanged(previousCustomerId, newCustomerId)) {
      // Different user returned from the login provider.
      // Persist the new ID first so the reload doesn't loop, then clear
      // every cache layer and navigate to the root URL.
      // We must NOT use location.reload() — the current URL still contains
      // the OIDC callback ?code=&state= params and the authorization code is
      // single-use; reloading would attempt to exchange the spent code and fail.
      return this.switchUser$(newCustomerId);
    }

    return from(this.settingsService.setLastLoggedInCustomerId$(newCustomerId)).pipe(map(() => ({ loginResponse, isLoginCallback })));
  }

  private hasUserChanged(previousCustomerId: string, newCustomerId: string): boolean {
    return previousCustomerId !== '' && previousCustomerId !== newCustomerId;
  }

  private switchUser$(newCustomerId: string): Observable<never> {
    return from(this.settingsService.setLastLoggedInCustomerId$(newCustomerId)).pipe(
      tap(() => this.imageApiService.clearDetailCache()),
      switchMap(() => from(this.imageProcessingService.deleteAll())),
      tap(() => {
        globalThis.location.href = globalThis.location.origin;
      }),
      mergeMap(() => EMPTY)
    );
  }
}
